import React from 'react';
import { useLocale } from '../../context/LocaleContext';
import styles from '../packaging.module.css';

const TickIcon = ({ size, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M10 3L4.5 9L2 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Figma icon/16/information-circle: an 18px circle with the "i" cut out, so the image shows through.
const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 8.95833C0 4.01078 4.01078 0 8.95833 0C13.9058 0 17.9167 4.01077 17.9167 8.95833C17.9167 13.9058 13.9058 17.9167 8.95833 17.9167C4.01077 17.9167 0 13.9058 0 8.95833ZM9.12075 7.31742C9.34208 7.35017 9.63225 7.43767 9.86958 7.69867C10.0966 7.94842 10.162 8.234 10.1869 8.43767C10.2085 8.61425 10.2084 8.82208 10.2083 9.0085V9.04167V12.7083C10.2083 13.1686 9.83525 13.5417 9.375 13.5417C8.91475 13.5417 8.54167 13.1686 8.54167 12.7083V9.04167V8.95833C8.08142 8.95833 7.70833 8.58525 7.70833 8.125C7.70833 7.66475 8.08142 7.29167 8.54167 7.29167L8.57567 7.29166C8.74083 7.29153 8.94458 7.29137 9.12075 7.31742ZM8.95833 4.79167C8.49808 4.79167 8.125 5.16477 8.125 5.625C8.125 6.08523 8.49808 6.45833 8.95833 6.45833C9.41858 6.45833 9.79175 6.08523 9.79175 5.625C9.79175 5.16477 9.41858 4.79167 8.95833 4.79167Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Selectable packaging card, shared by steps 1-3. Radio semantics on the row; the info button and any `field`
 * (always shown, under the row) sit outside the radio so they aren't nested controls.
 */
export function OptionCard({
  name,
  description = '',
  badge = null,
  price = 0,
  priceNote = null,
  heroImages = [],
  heroInset = false,
  thumbUrl = '',
  ticks = [],
  selected = false,
  onSelect,
  onInfo = null,
  field = null,
  children = null
}) {
  const { formatPrice } = useLocale();
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };
  const heroClass = [styles.hero, heroImages.length > 1 ? styles['hero--pair'] : '', heroInset ? styles['hero--inset'] : '']
    .filter(Boolean)
    .join(' ');

  // Figma puts the "i" on the row thumbnail when there is one, otherwise on the hero image.
  const infoButton = onInfo && (
    <button type="button" className={styles.info} aria-label={`More about ${name}`} onClick={onInfo}>
      <InfoIcon />
    </button>
  );

  return (
    <div className={`${styles.card} ${selected ? styles['card--selected'] : ''} ${badge ? styles['card--badged'] : ''}`}>
      {badge && <span className={styles.badge}>{badge}</span>}
      {onInfo && !thumbUrl && infoButton}
      {heroImages.length > 0 && (
        <div className={heroClass}>
          {heroImages.map((src) => (
            <img key={src} className={styles.heroImage} src={src} alt="" loading="lazy" />
          ))}
        </div>
      )}
      <div className={styles.details}>
        {thumbUrl && infoButton}
        <div
          role="radio"
          aria-checked={selected}
          tabIndex={0}
          className={`${styles.row} ${thumbUrl ? styles['row--thumb'] : ''}`}
          onClick={onSelect}
          onKeyDown={onKeyDown}
        >
          {thumbUrl && <img className={styles.thumb} src={thumbUrl} alt="" loading="lazy" />}
          <span className={styles.text}>
            <span className={styles.name}>{name}</span>
            {description && <span className={styles.desc}>{description}</span>}
          </span>
          <span className={styles.priceCol}>
            <span className={styles.price}>+{formatPrice({ amount: price })}</span>
            {priceNote && <span className={styles.priceNote}>{priceNote}</span>}
          </span>
          <span className={styles.radio} aria-hidden="true">
            {selected ? <TickIcon size={14} /> : null}
          </span>
        </div>
        {field && <div className={styles.body}>{field}</div>}
        {!selected && ticks.length > 0 && (
          <ul className={styles.ticks}>
            {ticks.map((tick) => (
              <li key={tick}>
                <TickIcon size={16} className={styles.tickIcon} />
                {tick}
              </li>
            ))}
          </ul>
        )}
        {selected && children && <div className={styles.body}>{children}</div>}
      </div>
    </div>
  );
}
